'use client'

import { useState, useRef, useEffect } from 'react'
import { ChevronDown, HelpCircle } from 'lucide-react'
import { useT } from '@/lib/useT'

const faqKeys = [
  { qKey: 'faq.faq1', aKey: 'faq.faq1Ans' },
  { qKey: 'faq.faq2', aKey: 'faq.faq2Ans' },
  { qKey: 'faq.faq3', aKey: 'faq.faq3Ans' },
  { qKey: 'faq.faq4', aKey: 'faq.faq4Ans' },
]

function FAQItem({
  faq,
  index,
  openIndex,
  setOpenIndex,
  t,
}: {
  faq: { qKey: string; aKey: string }
  index: number
  openIndex: number | null
  setOpenIndex: (i: number | null) => void
  t: (key: string) => string
}) {
  const isOpen = openIndex === index
  const bodyRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (!bodyRef.current) return
    setHeight(isOpen ? bodyRef.current.scrollHeight : 0)
  }, [isOpen])

  return (
    <div
      className={`rounded-[12px] border transition-all duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${
        isOpen
          ? 'border-[#003F47]/30 bg-white shadow-[0_2px_4px_rgba(10,23,29,0.05),0_8px_20px_rgba(10,23,29,0.08)]'
          : 'border-[#E2E4E5] bg-white shadow-[0_1px_2px_rgba(10,23,29,0.06)] hover:border-[#C2D2D5] hover:shadow-[0_1px_2px_rgba(10,23,29,0.06),0_2px_6px_rgba(10,23,29,0.06)]'
      }`}
    >
      <dt>
        <button
          className="flex w-full items-start justify-between px-6 py-5 text-left gap-4 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#FFC992]"
          onClick={() => setOpenIndex(isOpen ? null : index)}
          aria-expanded={isOpen}
          aria-controls={`faq-answer-${index}`}
          id={`faq-question-${index}`}
        >
          <span
            className={`text-base font-semibold leading-snug transition-colors duration-[140ms] ${
              isOpen ? 'text-[#003F47]' : 'text-[#0A171D] group-hover:text-[#003F47]'
            }`}
          >
            {t(faq.qKey as any)}
          </span>
          <span
            className={`shrink-0 mt-0.5 flex items-center justify-center w-6 h-6 rounded-full transition-all duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
              isOpen ? 'bg-[#003F47] text-[#FFFBF7]' : 'bg-[#FFF6E9] text-[#6C7377] group-hover:bg-[#E4ECED] group-hover:text-[#003F47]'
            }`}
            aria-hidden="true"
          >
            <ChevronDown
              size={14}
              className={`transition-transform duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isOpen ? 'rotate-180' : ''}`}
            />
          </span>
        </button>
      </dt>
      <dd
        id={`faq-answer-${index}`}
        role="region"
        aria-labelledby={`faq-question-${index}`}
        style={{ height, transition: 'height 360ms cubic-bezier(0.16, 1, 0.3, 1)' }}
        className="overflow-hidden"
      >
        <div ref={bodyRef} className="px-6 pb-5">
          <div className="h-px bg-[#E2E4E5] mb-4" aria-hidden="true" />
          <p className="text-sm text-[#6C7377] leading-relaxed">{t(faq.aKey as any)}</p>
        </div>
      </dd>
    </div>
  )
}

export default function FAQ() {
  const t = useT()
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section
      id="faq"
      className="py-24 bg-[#FFF6E9]"
      aria-labelledby="faq-heading"
    >
      <div className="mx-auto max-w-[800px] px-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[999px] bg-[#003F47]/8 text-[#003F47]">
            <HelpCircle size={14} aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.12em]">FAQ</span>
          </div>
          <h2
            id="faq-heading"
            className="text-3xl md:text-4xl font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('faq.heading')}
          </h2>
          <p className="text-base text-[#6C7377] leading-relaxed max-w-xl text-pretty">
            {t('faq.intro')}{' '}
            <a href="#" className="text-[#1A535A] font-medium underline underline-offset-2 hover:text-[#003F47] transition-colors">
              {t('faq.introLink')}
            </a>
            .
          </p>
        </div>

        <dl className="flex flex-col gap-3">
          {faqKeys.map((faq, i) => (
            <FAQItem
              key={faq.qKey}
              faq={faq}
              index={i}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              t={t}
            />
          ))}
        </dl>

        {/* Still have questions CTA */}
        <div className="mt-12 flex flex-col items-center gap-4 rounded-[18px] bg-white border border-[#E2E4E5] px-8 py-10 text-center shadow-[0_1px_2px_rgba(10,23,29,0.06),0_2px_6px_rgba(10,23,29,0.06)]">
          <p className="text-base font-semibold text-[#0A171D]">{t('faq.ctaHeading')}</p>
          <p className="text-sm text-[#6C7377] max-w-sm">
            {t('faq.ctaDescription')}
          </p>
          <a
            href="#"
            className="btn-press inline-flex items-center gap-2 px-6 py-2.5 rounded-[999px] text-sm font-semibold text-[#FFFBF7] bg-[#003F47] hover:bg-[#00343B] transition-all duration-[140ms] shadow-[0_12px_32px_rgba(0,63,71,0.22)]"
          >
            {t('faq.ctaButton')}
          </a>
        </div>
      </div>
    </section>
  )
}
